export enum DimensionClassOptions {
  QUANTITY = 'quantity',
  LENGTH = 'length',
  WEIGHT = 'weight',
  VOLUME = 'volume',
}

export const DimensionClassOptionLabels: {
  [key in DimensionClassOptions]: string;
} = {
  [DimensionClassOptions.QUANTITY]: 'Quantity',
  [DimensionClassOptions.LENGTH]: 'Length',
  [DimensionClassOptions.WEIGHT]: 'Weight',
  [DimensionClassOptions.VOLUME]: 'Volume',
};

export type DimensionClassType = {
  dimensionClass: DimensionClassOptions;
};

export type UnitOfMeasureType = {
  dimensionUnit: UnitOfMeasureOptions;
  dimensionValue: number;
} & DimensionClassType;

// Corresponds to common.enums.UnitOfMeasure
export enum UnitOfMeasureOptions {
  // Quantity (dimensionless) units #
  PIECE = 'piece',
  EACH = 'each',
  PAIR = 'pair',
  // Length units #
  INCH = 'inch',
  FOOT = 'foot',
  YARD = 'yard',
  METER = 'meter',
  CENTIMETER = 'centimeter',
  MILLIMETER = 'millimeter',
  MICROMETER = 'micrometer',
  MIL = 'mil',
  // Weight units #
  GRAM = 'gram',
  KILOGRAM = 'kilogram',
  MILLIGRAM = 'milligram',
  OUNCE = 'ounce',
  POUND = 'pound',
  // Volume units #
  MILLILITER = 'milliliter',
  LITER = 'liter',
  GALLON = 'gallon',
  QUART = 'quart',
  CUBIC_CENTIMETER = 'cubic_centimeter',
  CUBIC_METER = 'cubic_meter',
}

export const dimensionClassBaseUnit: {
  [key in DimensionClassOptions]: UnitOfMeasureOptions;
} = {
  [DimensionClassOptions.QUANTITY]: UnitOfMeasureOptions.PIECE,
  [DimensionClassOptions.LENGTH]: UnitOfMeasureOptions.MILLIMETER,
  [DimensionClassOptions.WEIGHT]: UnitOfMeasureOptions.MILLIGRAM,
  [DimensionClassOptions.VOLUME]: UnitOfMeasureOptions.MILLILITER,
};

export type UnitOfMeasureDetailsType = {
  label: string;
  abbreviation: string;
  dimensionClass: DimensionClassOptions;
  value: UnitOfMeasureOptions;
};

export const UnitOfMeasureDetails: {
  [key in UnitOfMeasureOptions]: UnitOfMeasureDetailsType;
} = {
  [UnitOfMeasureOptions.PIECE]: {
    label: 'Piece',
    abbreviation: 'pc',
    dimensionClass: DimensionClassOptions.QUANTITY,
    value: UnitOfMeasureOptions.PIECE,
  },
  [UnitOfMeasureOptions.EACH]: {
    label: 'Each',
    abbreviation: 'ea',
    dimensionClass: DimensionClassOptions.QUANTITY,
    value: UnitOfMeasureOptions.EACH,
  },
  [UnitOfMeasureOptions.PAIR]: {
    label: 'Pair',
    abbreviation: 'pr',
    dimensionClass: DimensionClassOptions.QUANTITY,
    value: UnitOfMeasureOptions.PAIR,
  },
  [UnitOfMeasureOptions.INCH]: {
    label: 'Inch',
    abbreviation: 'in',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.INCH,
  },
  [UnitOfMeasureOptions.FOOT]: {
    label: 'Foot',
    abbreviation: 'ft',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.FOOT,
  },
  [UnitOfMeasureOptions.YARD]: {
    label: 'Yard',
    abbreviation: 'yd',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.YARD,
  },
  [UnitOfMeasureOptions.METER]: {
    label: 'Meter',
    abbreviation: 'm',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.METER,
  },
  [UnitOfMeasureOptions.CENTIMETER]: {
    label: 'Centimeter',
    abbreviation: 'cm',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.CENTIMETER,
  },
  [UnitOfMeasureOptions.MILLIMETER]: {
    label: 'Millimeter',
    abbreviation: 'mm',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.MILLIMETER,
  },
  [UnitOfMeasureOptions.MICROMETER]: {
    label: 'Micrometer',
    abbreviation: 'μm',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.MICROMETER,
  },
  [UnitOfMeasureOptions.MIL]: {
    label: 'Mil',
    abbreviation: 'mil',
    dimensionClass: DimensionClassOptions.LENGTH,
    value: UnitOfMeasureOptions.MIL,
  },
  [UnitOfMeasureOptions.GRAM]: {
    label: 'Gram',
    abbreviation: 'g',
    dimensionClass: DimensionClassOptions.WEIGHT,
    value: UnitOfMeasureOptions.GRAM,
  },
  [UnitOfMeasureOptions.KILOGRAM]: {
    label: 'Kilogram',
    abbreviation: 'kg',
    dimensionClass: DimensionClassOptions.WEIGHT,
    value: UnitOfMeasureOptions.KILOGRAM,
  },
  [UnitOfMeasureOptions.MILLIGRAM]: {
    label: 'Milligram',
    abbreviation: 'mg',
    dimensionClass: DimensionClassOptions.WEIGHT,
    value: UnitOfMeasureOptions.MILLIGRAM,
  },
  [UnitOfMeasureOptions.OUNCE]: {
    label: 'Ounce',
    abbreviation: 'oz',
    dimensionClass: DimensionClassOptions.WEIGHT,
    value: UnitOfMeasureOptions.OUNCE,
  },
  [UnitOfMeasureOptions.POUND]: {
    label: 'Pound',
    abbreviation: 'lb',
    dimensionClass: DimensionClassOptions.WEIGHT,
    value: UnitOfMeasureOptions.POUND,
  },
  [UnitOfMeasureOptions.MILLILITER]: {
    label: 'Milliliter',
    abbreviation: 'ml',
    dimensionClass: DimensionClassOptions.VOLUME,
    value: UnitOfMeasureOptions.MILLILITER,
  },
  [UnitOfMeasureOptions.LITER]: {
    label: 'Liter',
    abbreviation: 'l',
    dimensionClass: DimensionClassOptions.VOLUME,
    value: UnitOfMeasureOptions.LITER,
  },
  [UnitOfMeasureOptions.GALLON]: {
    label: 'Gallon',
    abbreviation: 'gal',
    dimensionClass: DimensionClassOptions.VOLUME,
    value: UnitOfMeasureOptions.GALLON,
  },
  [UnitOfMeasureOptions.QUART]: {
    label: 'Quart',
    abbreviation: 'qt',
    dimensionClass: DimensionClassOptions.VOLUME,
    value: UnitOfMeasureOptions.QUART,
  },
  [UnitOfMeasureOptions.CUBIC_CENTIMETER]: {
    label: 'Cubic Centimeter',
    abbreviation: 'cc',
    dimensionClass: DimensionClassOptions.VOLUME,
    value: UnitOfMeasureOptions.CUBIC_CENTIMETER,
  },
  [UnitOfMeasureOptions.CUBIC_METER]: {
    label: 'Cubic Meter',
    abbreviation: 'm³',
    dimensionClass: DimensionClassOptions.VOLUME,
    value: UnitOfMeasureOptions.CUBIC_METER,
  },
};
